import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, MessageCircle, CopyIcon, ArrowLeft, X, AlertTriangle,
  ChevronDown, ChevronUp, Truck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOLLARS = (c) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Canonical order-date formatter used by the table + drawer.
 * Source field: `created_at` (there is no separate `ordered_at` — the
 * order is stamped on insert). Fallback: em dash for legacy rows or
 * malformed timestamps. Never derives from payment / video / shipping
 * timestamps. */
function formatOrderPlaced(iso) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// Header height owned by `Layout.jsx` — the site header is
// `sticky top-0 z-50` at `h-20 md:h-24`. The drawer + backdrop must
// begin below it so the sticky nav stays fully visible and clickable.
// Post Production-Defect-1 fix: the drawer no longer hard-codes an
// offset — it consumes the `--app-header-height` CSS variable that
// Layout.jsx publishes on mount/scroll/resize (which correctly accounts
// for the announcement bar, pitch-band, and sticky <header> together).

const PAYMENT_LABELS = {
  awaiting_payment:     "Awaiting Payment",
  payment_submitted:    "Payment Submitted",
  payment_needs_review: "Payment Needs Review",
  verified:             "Verified",
  failed:               "Failed",
  refunded:             "Refunded",
};
const VERIFICATION_LABELS = {
  video_pending:      "Video Pending",
  video_received:     "Video Received",
  video_under_review: "Under Review",
  approved:           "Approved",
  new_video_required: "New Video Required",
  rejected:           "Rejected",
};
const FULFILLMENT_LABELS = {
  waiting_for_verification: "Waiting for Verification",
  preparing_certificate:    "Preparing",
  ready_to_ship:            "Ready to Ship",
  shipped:                  "Order Was Shipped",
  completed:                "Completed",
  cancelled:                "Cancelled",
};

/** Semantic badge colour for a status value. Never colour-only —
 * every status also renders its human label. */
function badgeColour(kind, value) {
  const green = ["verified", "approved", "completed"];
  const yellow = ["payment_needs_review", "video_pending", "video_received",
    "video_under_review", "payment_submitted"];
  const red = ["failed", "rejected", "refunded", "new_video_required", "cancelled"];
  const blue = ["preparing_certificate", "ready_to_ship", "shipped"];
  if (green.includes(value)) return "bg-green-100 text-green-900 border-green-700";
  if (yellow.includes(value)) return "bg-amber-100 text-amber-900 border-amber-700";
  if (red.includes(value))    return "bg-red-100 text-red-900 border-red-700";
  if (blue.includes(value))   return "bg-sky-100 text-sky-900 border-sky-700";
  return "bg-slate-100 text-slate-800 border-slate-500";
}

/** Actions available for the given order state. Mirrors the backend
 * guards in `routers/certificate_orders.py::update_certificate_order`
 * so we never surface an option the API will refuse. */
function availableActions(order) {
  const p = order.payment_status;
  const v = order.verification_status;
  const f = order.fulfillment_status;

  const payment = [];
  if (p !== "verified")             payment.push({ code: "verified",             label: "Mark Payment Verified", dangerous: false });
  if (p !== "payment_needs_review") payment.push({ code: "payment_needs_review", label: "Mark Payment Needs Review", dangerous: false });
  if (p !== "failed" && p !== "refunded") payment.push({ code: "failed", label: "Mark Payment Failed", dangerous: true });
  if (p !== "refunded")             payment.push({ code: "refunded",             label: "Mark Refunded", dangerous: true });

  const verification = [];
  if (v === "video_pending")         verification.push({ code: "video_received", label: "Mark Video Received", dangerous: false });
  if (v === "video_received" || v === "new_video_required")
                                     verification.push({ code: "video_under_review", label: "Move to Under Review", dangerous: false });
  if (v === "video_under_review" || v === "video_received")
                                     verification.push({ code: "approved", label: "Approve Video", dangerous: false });
  if (v !== "new_video_required" && v !== "approved" && v !== "rejected")
                                     verification.push({ code: "new_video_required", label: "Request New Video", dangerous: true });
  if (v !== "rejected" && v !== "approved")
                                     verification.push({ code: "rejected", label: "Reject Verification", dangerous: true });

  // Fulfillment: backend requires payment=verified AND verification=approved
  // for `preparing_certificate`, `ready_to_ship`, `shipped`. `completed`
  // requires previous fulfillment=shipped.
  const canAdvancePrep = p === "verified" && v === "approved";
  const fulfillment = [];
  if (canAdvancePrep && f !== "preparing_certificate" && f !== "ready_to_ship" && f !== "shipped" && f !== "completed") {
    fulfillment.push({ code: "preparing_certificate", label: "Start Preparing", dangerous: false });
  }
  if (canAdvancePrep && (f === "preparing_certificate") && f !== "ready_to_ship") {
    fulfillment.push({ code: "ready_to_ship", label: "Mark Ready to Ship", dangerous: false });
  }
  if (canAdvancePrep && f === "ready_to_ship") {
    // Certificate orders ship via USPS Stamp Mail — no tracking number.
    // The transition itself is routine (payment=verified + video=approved
    // are already enforced above), so it is NOT marked `dangerous`; a
    // small confirmation still runs via `needsConfirm` so the admin
    // acknowledges the no-tracking notice before the write.
    fulfillment.push({ code: "shipped", label: "Mark as Shipped",
                        dangerous: false, needsTracking: false, needsConfirm: true });
  }
  if (f === "shipped") {
    fulfillment.push({ code: "completed", label: "Mark Completed", dangerous: true });
  }
  return { payment, verification, fulfillment };
}

const FILTERS = [
  { key: "needs_action",                  label: "Needs Action" },
  { key: "payment_needs_review",          label: "Payment Review", column: "payment_status" },
  { key: "video_review",                  label: "Video Review" },
  { key: "awaiting_certificate_assignment", label: "Assign Certificate", column: "operational_stage" },
  { key: "ready_to_fulfill",              label: "Ready to Fulfill" },
  { key: "waiting_on_customer",           label: "Waiting on Customer" },
  { key: "completed",                     label: "Completed", column: "fulfillment_status" },
  { key: "all",                           label: "All" },
];

// Aggregate URL-only filters. Not rendered as pills to keep the filter
// row clean — activated via `?filter=…` (dashboard deep-link) and shown
// with a highlighted banner so the admin can see which aggregate view
// is active and can clear it back to "All".
const AGGREGATE_FILTERS = {
  // P3.C · "Needs Action" is the canonical operational queue: any
  // order where the admin has SOMETHING to do — a manual override on
  // a customer-waiting stage, or a normal admin transition.
  needs_action: {
    label: "Needs Action",
    match: (o) => (o.waiting_on === "admin")
                    || (Array.isArray(o.admin_actions) && o.admin_actions.length > 0
                        && o.operational_stage !== "completed"
                        && o.operational_stage !== "cancelled"),
  },
  video_review: {
    label: "Video Review",
    match: (o) => o.verification_status === "video_received"
                    || o.verification_status === "video_under_review",
  },
  ready_to_fulfill: {
    label: "Ready to Fulfill",
    match: (o) => o.operational_stage === "preparing"
                    || o.operational_stage === "ready_to_ship",
  },
  pending_video_review: {  // legacy alias — kept for old bookmarks
    label: "Needs Video Review",
    match: (o) => o.verification_status === "video_received"
                    || o.verification_status === "video_under_review",
  },
  preparing: {
    label: "Preparing",
    match: (o) => o.operational_stage === "preparing",
  },
  waiting_on_customer: {
    label: "Waiting on Customer",
    match: (o) => o.waiting_on === "customer",
  },
};

/**
 * Certificate Purchasing · Admin dashboard tab.
 *
 * Rendered inside the shared public `Layout` (`App.js` route
 * `/admin/certificate-orders`) — that layout provides a
 * `sticky top-0 z-50 h-20 md:h-24` header. Historically this page
 * and its detail drawer both ignored the header offset, causing the
 * drawer to open behind the sticky nav. Fixed here by:
 *   · Page container padded to `pt-6 md:pt-8` (below Layout's normal spacing).
 *   · Detail drawer + backdrop start at `top-20 md:top-24` so the
 *     admin can always see and click the site header/logo.
 *   · Drawer has a sticky internal header + scrollable body + sticky
 *     footer so long orders scroll independently of the drawer chrome.
 *   · Prominent "Back to Admin Dashboard" link so admins are never
 *     stranded on this route.
 */
export default function AdminCertificateOrders() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isValidFilterKey = (k) =>
    FILTERS.some((f) => f.key === k) || Boolean(AGGREGATE_FILTERS[k]);
  // P3.C · Default landing is the operational "Needs Action" queue —
  // the owner should see what requires attention immediately, not the
  // full historical archive. Filter chips still expose the other
  // buckets including Completed / All.
  const initialFilter = searchParams.get("filter") || "needs_action";
  const [filter, setFilter] = useState(
    isValidFilterKey(initialFilter) ? initialFilter : "needs_action"
  );
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  // Keep the `?filter=…` param and internal state in sync so links from
  // the Admin Dashboard summary cards (`/admin/certificate-orders?filter=ready_to_ship`)
  // work as expected AND the admin can bookmark / share a filtered view.
  useEffect(() => {
    const q = searchParams.get("filter") || "needs_action";
    if (q !== filter && isValidFilterKey(q)) setFilter(q);
  }, [searchParams, filter]);
  const applyFilter = (key) => {
    setFilter(key);
    const next = new URLSearchParams(searchParams);
    if (key === "needs_action") next.delete("filter"); else next.set("filter", key);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/certificate-orders?limit=500`,
          { withCredentials: true }),
        axios.get(`${API}/admin/certificate-orders/summary`,
          { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setOrders(r.data.orders || []);
      setSummary(s.data || null);
    } catch (e) {
      toast.error("Failed to load certificate orders.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    const aggregate = AGGREGATE_FILTERS[filter];
    if (aggregate) return orders.filter(aggregate.match);
    const spec = FILTERS.find((f) => f.key === filter);
    if (!spec || !spec.column) return orders;
    return orders.filter((o) => o[spec.column] === filter);
  }, [orders, filter]);

  // P3.C · Row click → full-page workflow. Drawer removed.
  const openDetail = (o) => {
    navigate(`/admin/certificate-orders/${encodeURIComponent(o.public_order_number)}`);
  };

  const patch = async (payload, isShippingAction = false) => {
    if (!selected) return;
    try {
      const r = await axios.patch(
        `${API}/admin/certificate-orders/${selected.public_order_number}`,
        payload, { withCredentials: true });
      setSelected(r.data.order);
      if (isShippingAction) {
        // Map the structured email result the backend now returns.
        // Success-style ONLY for sent/already-sent. Everything else
        // uses warning/error styling — we never claim the email went
        // out when it did not. Status badge still flips to "Order Was
        // Shipped" regardless (backend already committed the write).
        const em = r.data?.email || {};
        const msg = em.message || "Order was shipped.";
        if (em.status === "sent" || em.status === "already_sent") {
          toast.success(msg);
        } else if (em.status === "failed") {
          toast.error(msg);
        } else {
          // not_configured / no_recipient / not_attempted
          toast.warning ? toast.warning(msg) : toast(msg, { icon: "⚠️" });
        }
      } else {
        toast.success("Updated.");
      }
      load();
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed.");
      return false;
    }
  };

  const goBackToAdmin = () => {
    // Prefer browser back so admins land on the tab they came from.
    // Fallback to /admin when there's no prior history (direct URL).
    if (window.history.length > 1 && document.referrer &&
        document.referrer.includes(window.location.host)) {
      navigate(-1);
    } else {
      navigate("/admin");
    }
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-cert-orders">
      {/* Page header row — back-nav + title + filters */}
      <div className="flex flex-col gap-3 mb-4">
        <button
          type="button"
          onClick={goBackToAdmin}
          className="inline-flex items-center gap-2 text-sm font-bold text-[var(--ink)] hover:text-[var(--panini-red)] w-fit"
          data-testid="certificate-orders-back-admin"
        >
          <ArrowLeft size={16} />
          Back to Admin Dashboard
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h1 className="font-serif text-2xl md:text-3xl" data-testid="admin-cert-orders-title">
            Certificate Orders
          </h1>
          <div className="flex flex-wrap gap-2" data-testid="admin-cert-orders-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => applyFilter(f.key)}
                data-testid={`admin-cert-filter-${f.key}`}
                className={`text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] ${filter === f.key ? "bg-[var(--panini-red)] text-white" : "bg-white"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[var(--smoke)]"><Loader2 className="animate-spin"/> Loading…</div>
      ) : (
        <>
          {AGGREGATE_FILTERS[filter] && (
            <div
              className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] px-4 py-2 text-sm"
              data-testid={`admin-cert-aggregate-banner-${filter}`}
            >
              <div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)] mr-2">
                  Aggregate filter
                </span>
                <span className="font-bold" data-testid="admin-cert-aggregate-banner-label">
                  {AGGREGATE_FILTERS[filter].label}
                </span>{" "}
                <span className="text-[var(--smoke)]">— showing {filtered.length} order{filtered.length === 1 ? "" : "s"}</span>
              </div>
              <button
                type="button"
                onClick={() => applyFilter("all")}
                className="btn text-xs"
                data-testid="admin-cert-aggregate-clear"
              >
                Clear filter
              </button>
            </div>
          )}
        <div className="overflow-x-auto rounded-2xl border-2 border-[var(--ink)] bg-white">
          <table className="w-full text-sm" data-testid="admin-cert-orders-table">
            <thead className="bg-[var(--panini-yellow-soft)] text-left uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-3 py-2">Order #</th>
                <th className="px-3 py-2">Ordered</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Album</th>
                <th className="px-3 py-2">Qty</th>
                <th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">Current Stage</th>
                <th className="px-3 py-2">Next Action</th>
                <th className="px-3 py-2">Shipping</th>
                <th className="px-3 py-2">Final Cert</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.public_order_number} onClick={() => openDetail(o)}
                    className="border-t border-[var(--ink)]/10 cursor-pointer hover:bg-[var(--panini-yellow-soft)]/40"
                    data-testid={`admin-cert-row-${o.public_order_number}`}>
                  <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">{o.public_order_number}</td>
                  <td
                    className="px-3 py-2 text-xs whitespace-nowrap text-[var(--smoke)]"
                    data-testid={`admin-cert-row-ordered-${o.public_order_number}`}
                  >
                    {formatOrderPlaced(o.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-bold">{o.customer?.first_name} {o.customer?.last_name}</div>
                    <div className="text-xs text-[var(--smoke)]">{o.customer?.email}</div>
                    <div className="text-xs text-[var(--smoke)]">{o.customer?.phone}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{o.album?.album_title || `${o.album?.year} ${o.album?.host_country}`}</td>
                  <td
                    className="px-3 py-2 font-bold"
                    data-testid={`admin-cert-row-qty-${o.public_order_number}`}
                  >
                    {(() => {
                      const q = Number(o.quantity ?? o.pricing?.quantity ?? 1);
                      return q > 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--panini-red)] text-white text-[11px]">
                          ×{q}
                        </span>
                      ) : (
                        <span className="text-[var(--smoke)]">×1</span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 font-bold">{DOLLARS(o.pricing?.total_cents ?? 1035)}</td>
                  <td className="px-3 py-2" data-testid={`admin-cert-row-stage-${o.public_order_number}`}>
                    <StageBadge stage={o.operational_stage} label={o.operational_stage_label} />
                    <div className="text-[10px] text-[var(--smoke)] mt-0.5"
                      title={`payment=${o.payment_status} · video=${o.verification_status} · fulfillment=${o.fulfillment_status}`}>
                      {o.waiting_on === "customer" ? "waiting on customer" : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2" data-testid={`admin-cert-row-next-${o.public_order_number}`}>
                    {o.next_action ? (
                      <span className="inline-block px-2 py-1 rounded-full bg-emerald-100 text-emerald-900 border-2 border-emerald-700 text-[11px] font-bold uppercase tracking-widest">
                        {o.next_action.label}
                      </span>
                    ) : (
                      <span className="text-[var(--smoke)]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`admin-cert-row-shipping-${o.public_order_number}`}>
                    {o.fulfillment_status === "shipped"
                      ? "USPS Stamp — No Tracking"
                      : "USPS Stamp Mail"}
                  </td>
                  <td className="px-3 py-2 text-xs">{o.final_certificate_number || "—"}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-[var(--smoke)]">No orders.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {selected && null}
    </div>
  );
}

function Badge({ kind, value }) {
  const label =
    kind === "payment"      ? (PAYMENT_LABELS[value] || value) :
    kind === "verification" ? (VERIFICATION_LABELS[value] || value) :
    kind === "fulfillment"  ? (FULFILLMENT_LABELS[value] || value) :
    value;
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-widest font-bold border-2 rounded-full px-2 py-0.5 ${badgeColour(kind, value)}`}
      data-testid={`status-chip-${value}`}
    >
      {String(label || "—").toString()}
    </span>
  );
}

// Owner-facing operational badge — one strong chip per row, driven by
// the shared `operational_stage` the backend derives (see
// `certificate_stage.derive_operational_stage`). The colour palette is
// picked so a scanning admin can instantly separate "waiting on
// customer" (cool grey) from work they own (warm accents) and terminal
// states (neutral).
const STAGE_TONE = {
  awaiting_payment:     "bg-slate-100  text-slate-800  border-slate-500",
  payment_needs_review: "bg-amber-100  text-amber-900  border-amber-700",
  awaiting_video:       "bg-slate-100  text-slate-800  border-slate-500",
  video_needs_review:   "bg-amber-100  text-amber-900  border-amber-700",
  new_video_required:   "bg-rose-100   text-rose-900   border-rose-700",
  awaiting_certificate_assignment: "bg-purple-100 text-purple-900 border-purple-700",
  preparing:            "bg-sky-100    text-sky-900    border-sky-700",
  ready_to_ship:        "bg-emerald-100 text-emerald-900 border-emerald-700",
  shipped:              "bg-neutral-200 text-neutral-800 border-neutral-600",
  completed:            "bg-neutral-200 text-neutral-800 border-neutral-600",
  cancelled:            "bg-neutral-200 text-neutral-500 border-neutral-500",
  unknown:              "bg-neutral-100 text-neutral-500 border-neutral-400",
};
function StageBadge({ stage, label }) {
  const tone = STAGE_TONE[stage] || STAGE_TONE.unknown;
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-widest font-bold border-2 rounded-full px-2 py-0.5 ${tone}`}
      data-testid={`stage-chip-${stage || "unknown"}`}
    >
      {label || stage || "—"}
    </span>
  );
}

/**
 * Right-side order-detail drawer. Positions itself BELOW the site
 * header (which is `sticky top-0 z-50` at `h-20 md:h-24` in
 * `Layout.jsx`) so the admin can still see and click the top nav
 * while the drawer is open. Backdrop shares the offset for the same
 * reason — it does NOT cover the header.
 *
 * The drawer body scrolls independently between a sticky header
 * (order number + close) and a sticky footer (Save Notes / Link
 * Certificate / WhatsApp) so long orders never bury the primary
 * actions.
 */
function Drawer({ order, onClose, onPatch, notifications }) {
  const c = order.customer || {};
  const ship = order.shipping_address || {};
  const alb = order.album || {};
  const p = order.pricing || {};
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [adminNote, setAdminNote] = useState(order.admin_note || "");
  const [customerNote, setCustomerNote] = useState(order.customer_note || "");
  const [finalNum, setFinalNum] = useState(order.final_certificate_number || "");
  const [finalUrl, setFinalUrl] = useState(order.final_certificate_url || "");
  const [pendingAction, setPendingAction] = useState(null); // { kind, code, label, dangerous, needsTracking }
  const [confirmReason, setConfirmReason] = useState("");
  const [confirmTracking, setConfirmTracking] = useState(order.tracking_number || "");

  const actions = availableActions(order);

  // Focus trap: close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const startAction = (kind, act) => {
    if (act.dangerous || act.needsTracking || act.needsConfirm) {
      setPendingAction({ kind, ...act });
      setConfirmReason("");
      setConfirmTracking(order.tracking_number || tracking || "");
    } else {
      applyAction(kind, act, "");
    }
  };

  const applyAction = async (kind, act, reason, trackingArg) => {
    const payload = { reason: reason || undefined };
    if (kind === "payment")      payload.payment_status = act.code;
    if (kind === "verification") payload.verification_status = act.code;
    if (kind === "fulfillment")  payload.fulfillment_status = act.code;
    // Certificate orders ship via USPS Stamp Mail — never require a
    // tracking number for this flow. Backend enforces the same.
    // Tracking field is intentionally omitted from the payload so we
    // do not persist a stale/empty value.
    // The `isShipping` flag lets the parent surface the specific email
    // delivery status (sent / not_configured / no_recipient / failed)
    // instead of a generic "Updated." toast.
    const isShipping = act.code === "shipped";
    const ok = await onPatch(payload, isShipping);
    if (ok) setPendingAction(null);
  };

  const closeBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      {/* Backdrop — begins BELOW the ENTIRE header stack (announcement
          bar + pitch-band + sticky <header>) via the CSS variable
          `--app-header-height` populated by Layout on mount / scroll /
          resize. Falls back to 96 px for environments that lack the
          variable (e.g. isolated storybook rendering). */}
      <div
        className="fixed left-0 right-0 bottom-0 z-40 bg-black/40"
        style={{ top: "var(--app-header-height, 96px)" }}
        onClick={closeBackdrop}
        data-testid="admin-cert-drawer-backdrop"
      />

      <aside
        className="fixed right-0 bottom-0 z-40 w-full md:max-w-3xl bg-white shadow-2xl flex flex-col border-l-2 border-[var(--ink)]"
        style={{
          top: "var(--app-header-height, 96px)",
          height: "calc(100vh - var(--app-header-height, 96px))",
        }}
        role="dialog" aria-modal="true"
        aria-labelledby="admin-cert-drawer-title"
        data-testid="admin-cert-drawer"
      >
        {/* Sticky drawer header — order # + close */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b-2 border-[var(--ink)] bg-white shrink-0"
             data-testid="admin-cert-drawer-header">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
              Certificate Order
            </div>
            <h2 id="admin-cert-drawer-title" className="font-serif text-lg md:text-xl truncate">
              {order.public_order_number}
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(order.public_order_number); toast.success("Copied."); }}
                className="ml-2 text-xs underline align-middle"
                data-testid="admin-cert-copy-order-num"
              >
                <CopyIcon size={12} className="inline" /> copy
              </button>
            </h2>
          </div>
          <button
            type="button" onClick={onClose}
            className="inline-flex items-center gap-1 text-sm font-bold text-[var(--smoke)] hover:text-[var(--ink)] p-1"
            aria-label="Close"
            data-testid="admin-cert-drawer-close"
          >
            <X size={18} />
            <span className="hidden md:inline">Close</span>
          </button>
        </div>

        {/* Scrollable drawer body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-4">
          {/* Section 1 — Status Summary */}
          <Section title="Status Summary" testid="admin-cert-status-summary">
            <div className="mb-3 flex items-center gap-3 flex-wrap">
              <StageBadge stage={order.operational_stage} label={order.operational_stage_label} />
              {order.waiting_on === "customer" && (
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
                  Waiting on customer
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <SummaryLine label="Payment"      kind="payment"      value={order.payment_status}      testid="summary-payment" />
              <SummaryLine label="Verification" kind="verification" value={order.verification_status} testid="summary-verification" />
              <SummaryLine label="Fulfillment"  kind="fulfillment"  value={order.fulfillment_status}  testid="summary-fulfillment" />
            </div>
            <div className="mt-3 text-xs" data-testid="admin-cert-order-placed">
              <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Order placed</span>{" "}
              <span className="font-bold">{formatOrderPlaced(order.created_at)}</span>
            </div>
          </Section>

          {/* Dominant next action — ONE big contextual button matching
              the operational stage. When the ball is with the customer
              (`waiting_on === "customer"`), we render a neutral notice
              instead of a button so the admin sees nothing to click. */}
          {order.next_action ? (
            <Section title="Next Action" testid="admin-cert-next-action-section">
              <div className="flex flex-wrap items-center gap-3">
                {order.operational_stage === "video_needs_review" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onPatch({ verification_status: "approved" })}
                      className="btn bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold border-2 border-[var(--ink)]"
                      data-testid="admin-cert-action-approve-video"
                    >
                      Approve Video
                    </button>
                    <button
                      type="button"
                      onClick={() => onPatch({ verification_status: "new_video_required" })}
                      className="btn border-2 border-[var(--panini-red)] text-[var(--panini-red)] bg-white text-sm font-bold"
                      data-testid="admin-cert-action-request-new-video"
                    >
                      Request New Video
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      order.operational_stage === "ready_to_ship"
                        ? startAction("fulfillment",
                            { code: "shipped", label: "Mark as Shipped",
                              dangerous: false, needsTracking: false, needsConfirm: true })
                        : onPatch(order.next_action.patch,
                            order.next_action.code === "mark_shipped")
                    }
                    className={`btn text-sm font-bold border-2 border-[var(--ink)] ${
                      order.operational_stage === "ready_to_ship"
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-[var(--panini-yellow)] hover:brightness-95 text-[var(--ink)]"
                    }`}
                    data-testid={`admin-cert-action-${order.next_action.code}`}
                  >
                    {order.next_action.label}
                  </button>
                )}
                {order.operational_stage === "preparing" && (
                  <span className="text-xs text-[var(--smoke)]">
                    Certificate is being prepared. When the envelope is ready, Mark Ready to Ship.
                  </span>
                )}
                {order.operational_stage === "video_needs_review" && (
                  <span className="text-xs text-[var(--smoke)]">
                    Review the completion video. Approving auto-advances fulfillment to Preparing.
                  </span>
                )}
              </div>
            </Section>
          ) : (
            <Section title="Next Action" testid="admin-cert-next-action-section">
              <div className="text-sm text-[var(--smoke)]" data-testid="admin-cert-action-none">
                {order.waiting_on === "customer"
                  ? "Waiting on the customer. No admin action required right now."
                  : "No admin action required."}
              </div>
            </Section>
          )}

          {/* Section 2 — Advanced / Correct Status (foldout — secondary)
              These raw dropdowns are ONLY used for exceptional cases:
              back-dating a state, reversing an approval, marking a
              legacy `waiting_for_verification` row that never got
              auto-advanced, etc. The normal workflow lives in Next
              Action above. */}
          <Section
            title="Advanced · Correct Status"
            testid="admin-cert-change-status"
            collapsible
            defaultCollapsed
          >
            <p className="mb-3 text-xs text-[var(--smoke)]">
              Use only if the normal Next Action does not apply — for example
              reversing an approval or repairing a legacy stuck state.
              Editing a status here does NOT trigger the customer-facing
              email for that stage.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <WorkflowCard
                title="Payment Status" kind="payment"
                current={order.payment_status} currentLabel={PAYMENT_LABELS[order.payment_status]}
                actions={actions.payment} onSelect={(a) => startAction("payment", a)}
              />
              <WorkflowCard
                title="Verification Status" kind="verification"
                current={order.verification_status} currentLabel={VERIFICATION_LABELS[order.verification_status]}
                actions={actions.verification} onSelect={(a) => startAction("verification", a)}
              />
              <WorkflowCard
                title="Fulfillment Status" kind="fulfillment"
                current={order.fulfillment_status} currentLabel={FULFILLMENT_LABELS[order.fulfillment_status]}
                actions={actions.fulfillment} onSelect={(a) => startAction("fulfillment", a)}
              />
            </div>
            {!actions.payment.length && !actions.verification.length && !actions.fulfillment.length && (
              <p className="mt-3 text-xs text-[var(--smoke)]" data-testid="admin-cert-no-actions">
                This order has no further valid transitions.
              </p>
            )}
          </Section>

          {/* Section 3 — Shipment Information */}
          <Section title="Shipment Information" testid="admin-cert-shipment-info" collapsible>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Shipping method</div>
                <div data-testid="admin-cert-shipping-method">USPS Stamp Mail — No Tracking</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Shipped at</div>
                <div data-testid="admin-cert-shipped-at">{order.shipped_at || "—"}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Ship to</div>
                <div>{ship.line1}{ship.line2 ? `, ${ship.line2}` : ""}</div>
                <div>{ship.city}{ship.city ? "," : ""} {ship.state} {ship.postal_code}</div>
                <div>{ship.country}</div>
              </div>
            </div>
          </Section>

          {/* Section 4 — Admin Notes */}
          <Section title="Admin Notes" testid="admin-cert-notes" collapsible>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <label>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Customer-visible note</span>
                <textarea
                  value={customerNote} onChange={(e) => setCustomerNote(e.target.value)}
                  className="gm-field w-full" rows={3} data-testid="admin-cert-customer-note"
                />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Private admin note</span>
                <textarea
                  value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                  className="gm-field w-full" rows={3} data-testid="admin-cert-admin-note"
                />
              </label>
            </div>
          </Section>

          {/* Section 5 — Certificate Information */}
          <Section title="Certificate Information" testid="admin-cert-final-cert" collapsible>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Album</div>
                <div className="font-bold">{alb.album_title || "—"}</div>
                <div>Certificate name: <strong>{alb.collector_display_name || "—"}</strong></div>
                <div>Completion: {alb.completion_date || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Pricing</div>
                <div className="grid grid-cols-2 gap-x-3">
                  <div>Certificate</div><div className="text-right">{DOLLARS(p.certificate_cents ?? 500)}</div>
                  <div>Shipping</div><div className="text-right">{DOLLARS(p.shipping_cents ?? 500)}</div>
                  <div>Tax (7%)</div><div className="text-right">{DOLLARS(p.tax_cents ?? 70)}</div>
                  <div className="font-bold">Total</div><div className="text-right font-bold">{DOLLARS(p.total_cents ?? 1070)}</div>
                </div>
              </div>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Final certificate number</span>
                <input value={finalNum} onChange={(e) => setFinalNum(e.target.value)}
                       className="gm-field w-full" data-testid="admin-cert-final-num" />
              </label>
              <label>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Final certificate URL</span>
                <input value={finalUrl} onChange={(e) => setFinalUrl(e.target.value)}
                       className="gm-field w-full" data-testid="admin-cert-final-url" />
              </label>
            </div>
          </Section>

          {/* Section 6 — Customer */}
          <Section title="Customer" testid="admin-cert-drawer-customer" collapsible>
            <div className="text-sm">
              <div className="font-bold">{c.first_name} {c.last_name}</div>
              <div>{c.email}</div>
              <div>{c.phone}</div>
              <div className="mt-2 text-xs text-[var(--smoke)]">
                {order.collector_id ? `Account: ${order.collector_id}` : `Guest: ${order.guest_id || "?"}`}
              </div>
            </div>
          </Section>

          {/* Section 7 — History */}
          <Section title="History" testid="admin-cert-drawer-history-section" collapsible defaultCollapsed>
            <ul className="text-xs space-y-1" data-testid="admin-cert-drawer-history">
              {(order.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="border-b border-[var(--ink)]/10 pb-1">
                  <span className="text-[var(--smoke)]">{h.at}</span> · <strong>{h.kind}</strong>
                  {h.detail?.from && <span> {String(h.detail.from)} → {String(h.detail.to)}</span>}
                  {h.detail?.reason && <em className="text-[var(--smoke)]"> — {h.detail.reason}</em>}
                </li>
              ))}
              {!(order.history || []).length && (
                <li className="text-[var(--smoke)]">No history yet.</li>
              )}
            </ul>
          </Section>

          {/* Section 8 — Notification History */}
          <Section title="Notification History" testid="admin-cert-drawer-notifications-section" collapsible defaultCollapsed>
            <ul className="text-xs" data-testid="admin-cert-drawer-notifications">
              {notifications.length === 0 && (
                <li className="text-[var(--smoke)]">No notifications recorded.</li>
              )}
              {notifications.map((n, i) => (
                <li key={i}>{n.at} · {n.kind} → {n.to}</li>
              ))}
            </ul>
          </Section>
        </div>

        {/* Sticky drawer footer — Save Notes + Link Certificate + WhatsApp */}
        <div className="border-t-2 border-[var(--ink)] bg-white px-5 py-3 flex flex-wrap gap-2 shrink-0"
             data-testid="admin-cert-drawer-footer">
          {order.fulfillment_status === "ready_to_ship" && (
            <button
              type="button"
              onClick={() => startAction("fulfillment",
                { code: "shipped", label: "Mark as Shipped",
                  dangerous: false, needsTracking: false, needsConfirm: true })}
              className="btn bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold border-2 border-[var(--ink)]"
              data-testid="admin-cert-mark-as-shipped"
            >
              <Truck size={14} className="inline mr-1" />
              Mark as Shipped
            </button>
          )}
          <button
            type="button"
            onClick={() => onPatch({ admin_note: adminNote, customer_note: customerNote })}
            className="btn text-xs"
            data-testid="act-save-notes"
          >
            Save Notes
          </button>
          <button
            type="button"
            onClick={() => onPatch({ final_certificate_number: finalNum, final_certificate_url: finalUrl })}
            className="btn text-xs"
            data-testid="act-link-final"
          >
            Link Final Certificate
          </button>
          {c.phone && (
            <a
              target="_blank" rel="noreferrer"
              href={`https://wa.me/${c.phone.replace(/\D/g, '')}`}
              className="btn text-xs inline-flex items-center gap-1"
              data-testid="admin-cert-open-wa"
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
          )}
        </div>
      </aside>

      {pendingAction && (
        <ConfirmActionModal
          order={order} pending={pendingAction}
          reason={confirmReason} onReasonChange={setConfirmReason}
          tracking={confirmTracking} onTrackingChange={setConfirmTracking}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => applyAction(pendingAction.kind, pendingAction, confirmReason, confirmTracking)}
        />
      )}
    </>
  );
}

/** Collapsible drawer section — every section keeps the same title
 * style so scanning the drawer is easy on mobile. */
function Section({ title, children, testid, collapsible = false, defaultCollapsed = false }) {
  const [open, setOpen] = useState(!defaultCollapsed);
  return (
    <section
      className="rounded-xl border-2 border-[var(--ink)]/15 bg-white"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => collapsible && setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 text-left ${collapsible ? "cursor-pointer" : "cursor-default"}`}
        aria-expanded={open}
        aria-label={`Toggle ${title}`}
      >
        <span className="kicker kicker-black text-[10px]">{title}</span>
        {collapsible && (open ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </section>
  );
}

function SummaryLine({ label, kind, value, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</span>
      <Badge kind={kind} value={value} />
    </div>
  );
}

/** One workflow card per axis (Payment / Verification / Fulfillment).
 * Shows the current status badge and a `Change Status` <select> +
 * `Apply` button. Statuses are mutually exclusive per axis so a
 * single-selection dropdown is the correct control (no checkboxes). */
function WorkflowCard({ title, kind, current, currentLabel, actions, onSelect }) {
  const [choice, setChoice] = useState("");
  const apply = () => {
    if (!choice) return;
    const act = actions.find((a) => a.code === choice);
    if (act) onSelect(act);
    setChoice("");
  };
  return (
    <div className="rounded-xl border-2 border-[var(--ink)] bg-white p-3"
         data-testid={`workflow-card-${kind}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-1">
        {title}
      </div>
      <div className="mb-3">
        <Badge kind={kind} value={current} />
        {currentLabel && (
          <span className="ml-2 text-xs text-[var(--smoke)]" data-testid={`workflow-current-${kind}`}>
            ({currentLabel})
          </span>
        )}
      </div>
      {actions.length > 0 ? (
        <>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-1"
                 htmlFor={`workflow-select-${kind}`}>
            Change Status
          </label>
          <select
            id={`workflow-select-${kind}`}
            value={choice} onChange={(e) => setChoice(e.target.value)}
            className="w-full mb-2 p-2 border-2 border-[var(--ink)] rounded-lg text-sm bg-white"
            data-testid={`workflow-select-${kind}`}
          >
            <option value="">Select action…</option>
            {actions.map((a) => (
              <option key={a.code} value={a.code}>
                {a.label}{a.dangerous ? " ⚠" : ""}
              </option>
            ))}
          </select>
          <button
            type="button" onClick={apply} disabled={!choice}
            className="btn bg-[var(--panini-red)] text-white text-xs w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`workflow-apply-${kind}`}
          >
            Apply
          </button>
        </>
      ) : (
        <p className="text-xs text-[var(--smoke)] italic" data-testid={`workflow-empty-${kind}`}>
          No further actions available.
        </p>
      )}
    </div>
  );
}

/** Confirmation modal shown BEFORE any dangerous status change or
 * before shipping (where a tracking number may be required). */
function ConfirmActionModal({
  order, pending, reason, onReasonChange,
  tracking, onTrackingChange, onCancel, onConfirm,
}) {
  const isShipping = pending.code === "shipped";
  const currentLabel =
    pending.kind === "payment"      ? (PAYMENT_LABELS[order.payment_status]      || order.payment_status) :
    pending.kind === "verification" ? (VERIFICATION_LABELS[order.verification_status] || order.verification_status) :
    pending.kind === "fulfillment"  ? (FULFILLMENT_LABELS[order.fulfillment_status]  || order.fulfillment_status) :
    "";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog" aria-modal="true"
      data-testid="admin-cert-confirm-modal"
    >
      <div className="bg-white rounded-2xl border-4 border-[var(--ink)] shadow-2xl max-w-lg w-full">
        <div className="flex items-start gap-3 p-4 border-b-2 border-[var(--ink)]">
          {isShipping ? (
            <Truck className="text-emerald-700 mt-0.5 shrink-0" size={20} />
          ) : (
            <AlertTriangle className="text-amber-700 mt-0.5 shrink-0" size={20} />
          )}
          <div className="min-w-0">
            <div className={`text-[10px] uppercase tracking-widest font-bold ${isShipping ? "text-emerald-700" : "text-[var(--panini-red)]"}`}>
              {isShipping ? "Confirm shipment" : "Confirm change"}
            </div>
            <h3 className="font-serif text-lg mt-0.5">
              {isShipping ? `Mark order ${order.public_order_number} as shipped?` : pending.label}
            </h3>
          </div>
        </div>
        <div className="p-4 text-sm space-y-3">
          {!isShipping && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <div className="text-[var(--smoke)]">Order</div>
              <div className="font-mono font-bold">{order.public_order_number}</div>
              <div className="text-[var(--smoke)]">Customer</div>
              <div>{order.customer?.first_name} {order.customer?.last_name}</div>
              <div className="text-[var(--smoke)]">Current status</div>
              <div className="font-bold">{currentLabel}</div>
              <div className="text-[var(--smoke)]">New status</div>
              <div className="font-bold text-[var(--panini-red)]">{pending.label}</div>
            </div>
          )}
          {isShipping && (
            <div className="rounded-lg border-2 border-emerald-700/40 bg-emerald-50/60 px-3 py-2 text-xs" data-testid="admin-cert-confirm-usps-notice">
              <div className="font-bold">Shipping method: USPS Stamp Mail — No Tracking</div>
              <div className="text-[var(--smoke)] mt-1">
                No tracking number will be recorded. The customer will be
                emailed as soon as this order is marked shipped.
              </div>
            </div>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
              {isShipping ? "Note (optional)" : `Reason ${pending.dangerous ? "(recorded in history)" : "(optional)"}`}
            </span>
            <textarea
              value={reason} onChange={(e) => onReasonChange(e.target.value)}
              rows={2} className="gm-field w-full mt-1"
              data-testid="admin-cert-confirm-reason"
            />
          </label>
        </div>
        <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 p-4 border-t-2 border-[var(--ink)]">
          <button
            type="button" onClick={onCancel}
            className="btn text-sm" data-testid="admin-cert-confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button" onClick={onConfirm}
            className={`btn text-sm text-white ${isShipping ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[var(--panini-red)]"}`}
            data-testid="admin-cert-confirm-apply"
          >
            {isShipping ? "Mark as Shipped" : "Confirm change"}
          </button>
        </div>
      </div>
    </div>
  );
}
